import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import {
  HOURS,
  MINUTES_5,
  closestMinuteIndex,
  compose,
  pad,
  parse,
  type ParsedTime,
  type Period,
} from './timeUtils';

export interface TimePickerProps {
  label?: string;
  /** "HH:mm" 24-hour format, or "" when empty. */
  value: string;
  /** Always emits "HH:mm" 24-hour format. */
  onChange: (time: string) => void;
  error?: boolean;
  labelIcon?: ReactNode;
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// TimePickerPanel
// ---------------------------------------------------------------------------

interface TimePickerPanelProps {
  hour12: number;
  minute: number; // raw 0..59 from prop
  period: Period;
  onChangeHour: (h: number) => void;
  onChangeMinuteIndex: (i: number) => void;
  onChangePeriod: (p: Period) => void;
  onRequestClose: () => void;
  /** Bounding rect of the trigger, used to anchor the popover on desktop. */
  anchorRect: DOMRect | null;
}

function TimePickerPanel({
  hour12,
  minute,
  period,
  onChangeHour,
  onChangeMinuteIndex,
  onChangePeriod,
  onRequestClose,
  anchorRect,
}: TimePickerPanelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const minuteIndex = closestMinuteIndex(minute);

  useEffect(() => {
    // Defer adding listeners to the next tick so the mousedown that opened
    // the panel is not immediately caught and the panel closed again.
    const timer = setTimeout(() => {
      function handleMouseDown(e: MouseEvent) {
        // Backdrop handles its own close via onMouseDown; ignore those events here
        // to prevent a double-fire of onRequestClose.
        if (e.defaultPrevented) return;
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target as Node)
        ) {
          onRequestClose();
        }
      }
      function handleKey(e: KeyboardEvent) {
        if (e.key === 'Escape') onRequestClose();
      }
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('keydown', handleKey);

      // Store cleanup refs so we can remove the right references.
      cleanupRef.current = () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('keydown', handleKey);
      };
    }, 0);

    return () => {
      clearTimeout(timer);
      cleanupRef.current?.();
    };
  }, [onRequestClose]);

  // Desktop popover position derived from the trigger rect. On mobile
  // the inline style is overridden by Tailwind via the `md:` reset.
  const isDesktop =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(min-width: 768px)').matches;
  const desktopStyle =
    isDesktop && anchorRect != null
      ? {
          top: anchorRect.bottom + window.scrollY + 4,
          left: anchorRect.left + window.scrollX,
          width: Math.max(anchorRect.width, 280),
        }
      : undefined;

  return createPortal(
    <>
      {/* Backdrop — only visible on mobile sheet; pointer-events on so taps close */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-[200] bg-black/30 md:hidden"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRequestClose();
        }}
      />
      <div
        ref={containerRef}
        role="dialog"
        aria-label="Select time"
        aria-modal="true"
        className={[
          // Mobile: bottom sheet
          'fixed inset-x-0 bottom-0 z-[200] rounded-t-xl border border-b-0 border-border',
          'bg-card text-card-foreground shadow-[0px_-4px_16px_-4px_rgba(0,0,0,0.1)]',
          'p-4 pb-[max(env(safe-area-inset-bottom),16px)]',
          // Desktop: detach to floating popover
          'md:fixed md:inset-x-auto md:bottom-auto md:z-[50]',
          'md:rounded-md md:border md:border-border md:p-3',
          'md:shadow-[0px_4px_16px_-4px_rgba(0,0,0,0.12)]',
        ].join(' ')}
        style={desktopStyle}
      >
        {/* Mobile header with title + Done */}
        <div className="flex items-center justify-between mb-3 md:hidden">
          <span className="text-sm font-medium text-foreground">
            Select time
          </span>
          <button
            type="button"
            onClick={onRequestClose}
            className="text-sm font-medium text-primary px-3 py-2 -mr-3"
          >
            Done
          </button>
        </div>

        <div className="flex gap-4 justify-center">
          <Column
            label="Hour"
            options={HOURS}
            selected={hour12}
            format={(h) => String(h)}
            onSelect={onChangeHour}
          />
          <Column
            label="Minute"
            options={MINUTES_5.map((_, i) => i)}
            selected={minuteIndex}
            format={(i) => pad(MINUTES_5[i])}
            onSelect={onChangeMinuteIndex}
          />
        </div>

        <div
          role="radiogroup"
          aria-label="AM or PM"
          className="mt-4 flex w-full rounded-full border border-border p-1 bg-card"
        >
          {(['AM', 'PM'] as Period[]).map((p) => {
            const active = p === period;
            return (
              <button
                key={p}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onChangePeriod(p)}
                className={[
                  'flex-1 rounded-full text-sm py-1.5 transition-colors',
                  active
                    ? 'bg-primary text-primary-foreground font-medium'
                    : 'text-foreground/70',
                ].join(' ')}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>
    </>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Column
// ---------------------------------------------------------------------------

interface ColumnProps {
  label: string;
  options: number[]; // raw values OR indices, see `format`
  selected: number;
  format: (value: number) => string;
  onSelect: (value: number) => void;
}

/**
 * Vertical scrolling list used inside the picker panel. Each row is rendered
 * as `<li role="option">` for AT compatibility.
 *
 * KNOWN LIMITATION: arrow-key navigation between options is not yet wired —
 * the spec calls for ArrowUp/ArrowDown/Home/End within a column, but the
 * initial release ships click-only. Tracked as a follow-up to this PR.
 */
function Column({ label, options, selected, format, onSelect }: ColumnProps) {
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLLIElement>('[aria-selected="true"]');
    // scrollIntoView may not exist in test environments (JSDOM).
    el?.scrollIntoView?.({ block: 'center' });
  }, [selected]);

  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-xs font-medium text-foreground/70 uppercase tracking-wide">
        {label}
      </span>
      <ul
        ref={listRef}
        role="listbox"
        aria-label={label}
        className="max-h-[200px] w-20 overflow-y-auto py-1 rounded-md border border-border bg-card"
      >
        {options.map((value) => {
          const active = value === selected;
          return (
            <li
              key={value}
              role="option"
              aria-selected={active}
              onClick={() => onSelect(value)}
              className={[
                'h-10 flex items-center justify-center text-sm cursor-pointer select-none',
                active
                  ? 'bg-primary/10 text-primary font-medium'
                  : 'text-foreground/70 hover:bg-muted/40',
              ].join(' ')}
            >
              {format(value)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TimePicker (main export)
// ---------------------------------------------------------------------------

/**
 * 12-hour time picker. Single button trigger that displays the selected
 * time and opens a picker popover (bottom sheet on mobile, floating popover
 * on desktop). Accepts and emits 24-hour "HH:mm" strings.
 *
 * Native `<input type="time">` ignores the page locale and follows the
 * user's OS, which means Spanish-locale users always see a 24-hour
 * picker. This component renders 12-hour everywhere while keeping the
 * external API on the 24-hour `HH:mm` string format consumers expect.
 */
export const TimePicker = forwardRef<HTMLDivElement, TimePickerProps>(
  (
    { label, value, onChange, error = false, labelIcon, disabled = false },
    ref,
  ) => {
    const baseId = useId();
    const parsed = parse(value);
    const hour12 = parsed?.hour12 ?? 12;
    const minute = parsed?.minute ?? 0;
    const period: Period = parsed?.period ?? 'AM';

    // Commits hour/minute/period changes from the popover panel.
    const emit = (next: Partial<ParsedTime>) => {
      onChange(
        compose(
          next.hour12 ?? hour12,
          next.minute ?? minute,
          next.period ?? period,
        ),
      );
    };

    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);

    const handleToggle = () => {
      if (!open && triggerRef.current) {
        setAnchorRect(triggerRef.current.getBoundingClientRect());
      }
      setOpen((prev) => !prev);
    };

    const display = parsed
      ? `${parsed.hour12}:${pad(parsed.minute)}${parsed.period}`
      : 'Select time';

    return (
      <div
        ref={ref}
        className="flex flex-col gap-1.5 w-full"
      >
        {label && (
          <span
            id={`${baseId}-label`}
            className="text-sm font-medium text-foreground leading-5 flex items-center gap-1.5"
          >
            {labelIcon}
            {label}
          </span>
        )}
        <button
          ref={triggerRef}
          type="button"
          id={`${baseId}-trigger`}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-labelledby={label ? `${baseId}-label` : undefined}
          aria-label={label ? undefined : `Time: ${display}`}
          aria-invalid={error || undefined}
          disabled={disabled}
          onClick={handleToggle}
          className={[
            'flex h-10 items-center gap-2 rounded-md border bg-background px-3 py-2',
            'text-sm text-foreground text-left',
            'focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-border-focus',
            'transition-colors duration-150',
            error ? 'border-border-error' : 'border-border',
            disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <span
            className={[
              'flex-1 min-w-0 truncate',
              parsed ? 'text-foreground' : 'text-muted-foreground',
            ].join(' ')}
          >
            {display}
          </span>
          <Clock
            size={20}
            className="text-icon-default shrink-0"
            aria-hidden="true"
          />
        </button>
        {open && !disabled && (
          <TimePickerPanel
            hour12={hour12}
            minute={minute}
            period={period}
            onChangeHour={(h) => emit({ hour12: h })}
            onChangeMinuteIndex={(i) => emit({ minute: MINUTES_5[i] })}
            onChangePeriod={(p) => emit({ period: p })}
            onRequestClose={() => setOpen(false)}
            anchorRect={anchorRect}
          />
        )}
      </div>
    );
  },
);

TimePicker.displayName = 'TimePicker';
